/** @format */

import { memo } from 'react'
import { CopyBlock, shadesOfPurple } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import build_1Img from 'assets/images/build-1.png'
import build_2Img from 'assets/images/build-2.png'
import html_css_jsImg from 'assets/images/html-css-js.png'
import deferImg from 'assets/images/defer.png'
import asyncImg from 'assets/images/async.png'

export const _6 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>6 - Процесс построения веб-страницы</h2>
    <p className={styles.description}>
      Когда браузер создает DOM, если он встречает тег <code className={styles.code}>script</code> в HTML, он должен
      выполнить его сразу. Если скрипт является внешним, он должен сначала загрузить скрипт.{' '}
    </p>
    <p className={styles.description}>
      По умолчанию, чтобы выполнить скрипт, построение DOM приостанавливается, и возобновляется только после того, как
      JavaScript-движок выполнил скрипт.{' '}
    </p>
    <img className={styles.img} src={build_1Img} alt="build-1" />
    <p className={styles.description}>
      Почему построение DOM приостановается? Скрипты могут изменять как HTML, так и его продукт - DOM, добавляя узлы.
      Скрипты могут также запрашивать что-то о DOM, и если это происходит, когда DOM все еще строится, он может вернуть
      неожиданные результаты.{' '}
    </p>
    <p className={styles.description}>
      JavaScript блокирует построение DOM, поскольку он может модифицировать документ. CSS не может изменить документ,
      поэтому кажется, что нет причин для его блокировки, правильно?{' '}
    </p>
    <p className={styles.description}>
      Однако, что если скрипт запрашивает информацию (о стиле), которая еще не была проанализирована? Браузер не знает,
      что собирается выполнить скрипт - он может запросить что-то вроде фонового цвета DOM-узла, который зависит от
      таблицы стилей.{' '}
    </p>
    <img className={styles.img} src={html_css_jsImg} alt="html-css-js" />
    <p className={styles.description}>
      Из-за этого CSS может блокировать разбор HTML в зависимости от порядка внешних таблиц стилей и сценариев в
      документе. Если перед скриптами в документе есть внешние таблицы стилей, конструкция объектов DOM и CSSOM может
      мешать друг другу.{' '}
    </p>
    <p className={styles.description}>
      Когда синтаксический анализатор попадает в тег сценария, конструкция DOM не может продолжаться до тех пор, пока
      JavaScript не завершит выполнение, и JavaScript не будет выполнен до тех пор, пока CSS не будет загружен,
      проанализирован и не будет доступен CSSOM.{' '}
    </p>
    <img className={styles.img} src={build_2Img} alt="build-2" />
    <p className={styles.description}>
      Еще одна вещь, о которой следует помнить, заключается в том, что даже если CSS не блокирует конструкцию DOM, он
      блокирует рендеринг. Браузер ничего не отобразит, пока не будет DOM и CSSOM. Это связано с тем, что страницы без
      CSS часто непригодны для использования. Если браузер показал беспорядочную страницу без CSS, а через несколько
      мгновений стилизованную, это называется <b>Flash of Unstyled Content</b>.{' '}
    </p>
    <p className={styles.description}>
      Внезапные смещения содержимого и визуальные изменения плохо влияют на <b>UX (user experience)</b>. Чтобы обойти
      эти проблемы, вы должны стремиться как можно быстрее доставить CSS. Помните правило «стили наверху, сценарии
      внизу»? Теперь вы знаете зачем делать именно так.{' '}
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>6.1 - Атрибуты defer и async</h3>
      <p className={styles.description}>
        Синхронные скрипты блокирующие парсер, это проблема. И не все скрипты одинаково важны для пользователей,
        например, для отслеживания и аналитики. Решение? Асинхронная загрузка этих менее важных скриптов.{' '}
      </p>
      <p className={styles.description}>
        Атрибуты <code className={styles.code}>defer</code> и <code className={styles.code}>async</code> были введены,
        чтобы дать разработчикам возможность рассказать браузеру, какие скрипты обрабатывать асинхронно.{' '}
      </p>
      <p className={styles.description}>
        Оба атрибута сообщают браузеру, что он может продолжить разбор HTML при загрузке сценария в фоновом режиме, а
        затем выполнить скрипт после его загрузки. Таким образом, загрузка скриптов не блокирует конструкцию DOM и
        рендеринг страниц. Результат: пользователь может видеть страницу до того, как все сценарии завершили загрузку.{' '}
      </p>
      <p className={styles.description}>
        Разница между ними - это тот момент, когда загруженые скрипты начинают выполняться.{' '}
      </p>
      <p className={styles.description}>
        Выполнение <code className={styles.code}>defer</code> скриптов начинается после завершения парсинга, но перед
        событием <code className={styles.code}>DOMContentLoaded</code>. Это гарантирует, что скрипты будут выполняться в
        том порядке, в котором они отображаются в HTML, и не будут блокировать синтаксический анализатор.{' '}
      </p>
      <CopyBlock
        text={`<script src="path-to-script.js" defer></script>`}
        language="html"
        showLineNumbers
        theme={shadesOfPurple}
      />
      <img className={styles.img} src={deferImg} alt="defer" />
      <p className={styles.description}>
        <code className={styles.code}>async</code> выполняется при первой возможности после завершения загрузки и перед
        событием загрузки окна. Это означает, что возможно скрипты не выполняются в том порядке, в котором они
        отображаются в HTML. Это также означает, что они могут прервать создание DOM.
      </p>
      <CopyBlock
        text={`<script src="path-to-script.js" async></script>`}
        language="html"
        showLineNumbers
        theme={shadesOfPurple}
      />
      <img className={styles.img} src={asyncImg} alt="async" />
      <p className={styles.description}>
        Где бы они ни были указаны, асинхронные скрипты загружаются с низким приоритетом. Они часто загружаются после
        всех других скриптов, не блокируя создание DOM. Однако, если <code className={styles.code}>async</code> скрипт
        завершает загрузку раньше, его выполнение может блокировать создание DOM и все синхронные скрипты, которые
        впоследствии завершают загрузку.{' '}
      </p>
    </section>
  </section>
))
